import React, { RefObject, useCallback, useMemo, useRef } from 'react';

import { AdvancedTooltip } from '@tanium/react-tooltip';

import { useTooltipRenderer } from '../../../hooks/useTooltipRenderer';
import { TooltipRenderFn } from '../../../utils/ChartProps';
import { ColorScale } from '../../../utils/ColorScale';
import { DataKeyType, LabelGetterProp } from '../../../utils/DataProps';
import { noPointerEventsTooltipModifiers } from '../../../utils/noPointerEventsTooltipModifiers';
import { NumberCoercible } from '../../../utils/NumberCoercible';
import { NumberScale } from '../../../utils/NumberScale';
import { PointInfo } from '../../../utils/PointInfo';
import { TimeScale } from '../../../utils/TimeScale';
import { GeneratedPointInfo } from './useInteractiveOverlay';

const TOOLTIP_POINT_OFFSET_PX = 12;

interface UseTooltipParams<TDataKeyType extends DataKeyType, TPointType, TDatum> extends LabelGetterProp<TDatum> {
  /**
   * Data about the active interactive point, if any. If not specified, no tooltip will be
   * generated.
   */
  activePointInfo: GeneratedPointInfo<TDataKeyType, TPointType, TDatum> | undefined;
  /**
   * The color scale to use.
   */
  colorScale: ColorScale<TDataKeyType>;
  /**
   * Whether or not to display a `Total` section at the bottom of the tooltip.
   */
  showTotal: boolean;
  /**
   * Whether or not to display a label percentage for each data item value.
   */
  showPercentage: boolean;
  /**
   * Whether or not to display times (as well as dates).
   */
  showTime: boolean;
  /**
   * Whether to sort the data by value before passing to the tooltip renderer. This should be set to
   * `true` when the values are not stacked and `false` when they are, so as to ensure the data is
   * presented in a visually consistent order.
   */
  sortData: boolean;
  /**
   * A title to display at the top of the tooltip.
   */
  title: React.ReactNode;
  /**
   * A render function for displaying a tooltip. If omitted, a default renderer is used. Specify
   * `false` to disable tooltips.
   */
  tooltipRenderer?: TooltipRenderFn<TDataKeyType, unknown> | false;
  /**
   * The x-scale used for display.
   */
  xScale: TimeScale;
  /**
   * The y-scale used for display.
   */
  yScale: NumberScale;
}

export interface UseTooltipReturnType {
  /**
   * A ref to attach to the SVG containing the chart. Its bounding rect is expected to coincide with
   * the x- and y-scale ranges.
   */
  svgRef: RefObject<SVGSVGElement>;
  /**
   * A React node containing the tooltip, if one is active.
   */
  tooltip: React.ReactNode;
}

export const useTooltip = <TDataKeyType extends DataKeyType, TPointType, TDatum>({
  activePointInfo,
  colorScale,
  labelGetter,
  showPercentage,
  showTime,
  showTotal,
  sortData,
  title,
  tooltipRenderer: tooltipRendererProp,
  xScale,
  yScale,
}: UseTooltipParams<TDataKeyType, TPointType, TDatum>): UseTooltipReturnType => {
  const svgRef = useRef<SVGSVGElement>(null);

  const tooltipRenderer = useTooltipRenderer<
    TDataKeyType,
    number,
    [dataKey: TDataKeyType, point: PointInfo<TPointType, TDatum, NumberCoercible>]
  >({
    dataKeyGetter: useCallback((d) => d[0], []),
    labelGetter: useCallback((d) => labelGetter(d[1].series), [labelGetter]),
    showOtherItems: true,
    showPercentage,
    showTime,
    showTotal,
    title,
    tooltipRenderer: tooltipRendererProp,
    valueGetter: useCallback((d) => d[1].value.valueOf(), []),
  });

  const tooltipProps = useMemo(() => {
    if (!activePointInfo || !tooltipRenderer) {
      return undefined;
    }

    const { generatedPoint } = activePointInfo;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- scaled value is based on active point, so should be defined
    const scaledX = xScale(generatedPoint.data[0])!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- scaled value is based on active point, so should be defined
    const scaledY = yScale(generatedPoint[1])!;

    const svgRect = svgRef.current?.getBoundingClientRect();
    const xOffset = svgRect?.left ?? 0;
    const yOffset = svgRect?.top ?? 0;

    const x = scaledX + xOffset;
    const y = scaledY + yOffset;

    return {
      referenceElement: {
        getBoundingClientRect: () => ({
          width: TOOLTIP_POINT_OFFSET_PX,
          height: TOOLTIP_POINT_OFFSET_PX,
          top: y - TOOLTIP_POINT_OFFSET_PX / 2,
          right: x + TOOLTIP_POINT_OFFSET_PX / 2,
          bottom: y + TOOLTIP_POINT_OFFSET_PX / 2,
          left: x - TOOLTIP_POINT_OFFSET_PX / 2,
        }),
        clientWidth: TOOLTIP_POINT_OFFSET_PX,
        clientHeight: TOOLTIP_POINT_OFFSET_PX,
      },
    };
  }, [activePointInfo, tooltipRenderer, xScale, yScale]);

  const tooltipContent = useMemo(() => {
    if (!activePointInfo || !tooltipRenderer) {
      return undefined;
    }

    const { generatedPoint, seriesDataKey } = activePointInfo;

    const tooltipData = Array.from(generatedPoint.data[1]).filter(
      (dataPoint): dataPoint is [dataKey: TDataKeyType, point: PointInfo<TPointType, TDatum, NumberCoercible>] => {
        return dataPoint[1].value !== undefined;
      },
    );

    const sortedData = sortData
      ? tooltipData.sort((a, b) => {
          return b[1].value.valueOf() - a[1].value.valueOf();
        })
      : tooltipData;

    return tooltipRenderer({
      activeDataKey: seriesDataKey,
      colorScale,
      data: sortedData,
      date: activePointInfo.generatedPoint.data[0].valueOf(),
    });
  }, [activePointInfo, colorScale, sortData, tooltipRenderer]);

  const tooltip = tooltipContent && (
    <AdvancedTooltip
      placement="top"
      modifiers={noPointerEventsTooltipModifiers}
      __UNSAFE_ADDITIONAL_PROPS__={tooltipProps}
    >
      {tooltipContent}
    </AdvancedTooltip>
  );

  return {
    svgRef,
    tooltip,
  };
};
